import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.actions';
import { FieldUpdateHandlerParam } from 'shared/form';
import * as types from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as selectors from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import {
  FormTemplate, FormTemplateField, FormTemplateOption, FormTemplateSection
} from 'data/template/template.types';
import {
  adaptInterfacemSectionFormData, adaptTemplateBasedOnSections, updateTemplateFieldsValues, adaptTemplateForNEType,
  adaptFormDataForNEType
} from 'routes/TargetDeliveryTemplateAccessFunction/utils/targetDeliveryTemplateFields.adapters';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { TargetTemplateFormSection } from 'routes/TargetDeliveryTemplateAccessFunction/TargetDeliveryTemplateAccessFunction.types';
import { KeyValuePair } from 'data/types';
import TargetDeliveryTemplateAccessFunctionForm from '../../components/TargetDeliveryTemplateAccessFunctionForm';
import { createTargetDeliveryTemplateBasedOnTemplate } from '../../utils/targetDeliveryTemplate.factory';
import {
  adaptTemplateOptions,
  adaptTemplateVisibilityMode,
  getTargetTemplateToSubmit,
  getAuditDetailsToSubmit,
} from '../../utils/targetDeliveryTemplate.adapter';
import targetTemplates from '../../templates';

type RouterParams = {
  history: string
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.TargetDeliveryTemplateAccessFunctionFormData
}

const mapState = (state: RootState) => ({
  selectedTargetDeliveryTemplate: selectors.getSelectedTargetDeliveryTemplateAccessFunction(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  deliveryFunctionId: state.global.selectedDF,
  privileges: state.authentication.privileges,
  beforeLogout: state.authentication.beforeLogout,
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
});

const mapDispatch = {
  goToTargetDeliveryTemplateAccessFunctionList: actions.goToTargetDeliveryTemplateAccessFunctionList,
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
  goToTargetDeliveryTemplateAccessFunctionCreate: actions.goToTargetDeliveryTemplateAccessFunctionCreate,
  goToTargetDeliveryTemplateAccessFunctionEdit: actions.goToTargetDeliveryTemplateAccessFunctionEdit,
  goToTargetDeliveryTemplateAccessFunctionView: actions.goToTargetDeliveryTemplateAccessFunctionView,
  selectTargetDeliveryTemplateAccessFunction: actions.selectTargetDeliveryTemplateAccessFunction,
  getTargetDeliveryTemplateAccessFunctionList: actions.getTargetDeliveryTemplateAccessFunctionList,
  deleteTargetDeliveryTemplateAccessFunction: actions.deleteTargetDeliveryTemplateAccessFunction,
  createTargetDeliveryTemplateAccessFunction: actions.createTargetDeliveryTemplateAccessFunction,
  getSupportedFeature: warrantActions.getWarrantFormConfig,
  getTemplates: warrantActions.getAllTemplates,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const TargetDeliveryTemplateAccessFunctionCreate = ({
  deliveryFunctionId,
  history,
  accessFunctions,
  enabledAccessFunctions,
  isAuditEnabled,
  userId,
  userName,
  goToTargetDeliveryTemplateAccessFunctionList,
  createTargetDeliveryTemplateAccessFunction,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [templates, setTemplates] = useState<{ [key: string]: FormTemplate }>({});
  const [template, setTemplate] = useState<FormTemplate>();
  const [formData, setFormData] = useState<Partial<types.TargetDeliveryTemplateAccessFunctionFormData>>({});
  const { t } = useTranslation();

  useEffect(() => {
    const availableTemplates = adaptTemplateOptions(
      targetTemplates,
      accessFunctions,
      enabledAccessFunctions,
    );

    setTemplates(availableTemplates);

    let newFormData = formData as types.TargetDeliveryTemplateAccessFunction;
    const templateKey = formData.afType ?? Object.keys(availableTemplates)[0];
    const adaptedTemplate = adaptTemplateVisibilityMode(availableTemplates[templateKey], 'create');

    if (newFormData.afType == null) {
      newFormData = createTargetDeliveryTemplateBasedOnTemplate(
        adaptedTemplate,
      ) as types.TargetDeliveryTemplateAccessFunction;
      setFormData(newFormData);
    }

    setTemplate(adaptedTemplate);

  }, [
    accessFunctions,
    enabledAccessFunctions,
  ]);

  const handleFieldUpdate = (
    { name, value }: FieldUpdateHandlerParam,
    formSection: TargetTemplateFormSection,
    interfaceId?: string,
  ) => {

    let clonedData = cloneDeep(formData);
    let clonedTemplate = cloneDeep(template);

    setFormData({
      ...clonedData,
      [name]: value,
    });

    if (formSection === 'main') {
      if (name === 'afId' && (clonedTemplate as FormTemplate).key === 'SIEMENS_LIMS') {
        const chosenAccessFunction = accessFunctions.find((af) => af.id === value);

        let leaOptions: FormTemplateOption[] = [];

        if (chosenAccessFunction) {
          const leas: string[] = (chosenAccessFunction.dataInterfaces ?? [])
            .flatMap((df) => df.options)
            .filter((o) => ['lea', 'leacs', 'leaps'].includes(o.key))
            .map((o) => o.value as string);

          leaOptions = [
            ...leas.map((lea) => {
              return {
                label: lea,
                value: lea
              };
            })
          ];
        }

        ((clonedTemplate as FormTemplate).ICD as FormTemplateSection).fields.LEA.options = leaOptions;
        ((clonedTemplate as FormTemplate).ICD as FormTemplateSection).fields.LEA.initial = leaOptions[0]?.value;

        (clonedTemplate as FormTemplate).formId = Date.now().toString();
      }

      if (name === 'afType') {
        const adaptedTemplate = adaptTemplateVisibilityMode(templates[value], 'create');

        const newFormData = createTargetDeliveryTemplateBasedOnTemplate(
          adaptedTemplate,
        ) as types.TargetDeliveryTemplateAccessFunction;

        setFormData({
          ...newFormData,
          [name]: value,
        });

        setTemplate(cloneDeep(templates[value]));
        return;
      }
    } else {
      ((clonedTemplate as FormTemplate)[formSection] as FormTemplateSection).fields[name].initial = value;
      if (((clonedTemplate as FormTemplate).key === 'SIEMENS_LIMS')) {
        if (formSection === 'networkType' || formSection === 'callContentType' || name === 'ACC') {
          const acc = name === 'ACC' ? value : (clonedData.targetInformation as types.TargetDeliveryTemplateInterfaceFormData[])[0].ACC as string;
          updateTemplateFieldsValues(clonedTemplate as FormTemplate, acc);
        }
      }

      if ((clonedTemplate as FormTemplate).key === 'ERICSSON_NE' && name === 'netype') {
        clonedTemplate = adaptTemplateForNEType(clonedTemplate as FormTemplate, value);
        clonedData = adaptFormDataForNEType(clonedTemplate, clonedData)
      }

      if ((clonedTemplate as FormTemplate).key === 'RIBBONC20_CSC' && name === 'definition') {
        if (value === 'PAIRED') {
          ((clonedTemplate as FormTemplate).usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Associate\'s DN';
          ((clonedTemplate as FormTemplate).usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'false';
        } else {
          ((clonedTemplate as FormTemplate).usnbdCallContentChannelInformation as FormTemplateSection).fields.dndest.label = 'Combined DN';
          ((clonedTemplate as FormTemplate).usnbdCallContentChannelInformation as FormTemplateSection).fields.dnsrc.hide = 'true';
        }
      }

      clonedData[formSection] = adaptInterfacemSectionFormData(
        clonedData[formSection],
        interfaceId,
        name,
        value,
      );

      clonedTemplate = adaptTemplateBasedOnSections(
        clonedTemplate,
        name,
        value,
        formSection,
      );

      (clonedTemplate as FormTemplate).formId = Date.now().toString();

      setFormData(clonedData);
    }
    setTemplate(clonedTemplate);
  };

  const handleClickSave = () => {
    const isValid = formRef?.current?.isValid(true, true) ?? true;

    if (!isValid) {
      return;
    }

    const formDataValues = formRef?.current?.getValues();

    if (!formDataValues) {
      return;
    }
    const submitData = getTargetTemplateToSubmit(formDataValues);

    const payload = {
      data: getTargetTemplateToSubmit(formDataValues),
      auditDetails: getAuditDetailsToSubmit(submitData, null, isAuditEnabled, userId, userName, 'CREATE'),
    };

    createTargetDeliveryTemplateAccessFunction(
      deliveryFunctionId,
      payload,
      history,
    );
  };

  const handleCancel = () => goToTargetDeliveryTemplateAccessFunctionList(history);

  const handleInterfaceAdd = (formSection: TargetTemplateFormSection) => {
    const clonedData = cloneDeep(formData);

    const newData = createTargetDeliveryTemplateBasedOnTemplate(template, true);
    const newInterfaceData = (newData[formSection] as types.TargetDeliveryTemplateInterfaceFormData[])[0];
    (clonedData[formSection] as types.TargetDeliveryTemplateInterfaceFormData[]).push(newInterfaceData);

    setFormData(clonedData);
  };

  const handleInterfaceRemove = (tabId: string | number, formSection: TargetTemplateFormSection) => {
    const clonedData = cloneDeep(formData);

    clonedData[formSection] = clonedData[formSection]?.filter(({ id }) => id !== tabId);

    setFormData(clonedData);
  };

  if (!template) {
    return <Fragment />;
  }

  return (
    <div data-testid="TargetDeliveryTemplateAccessFunctionCreate">
      <TargetDeliveryTemplateAccessFunctionForm
        ref={formRef}
        isEditing
        template={template}
        data={formData}
        title={t('targetDeliveryTemplateAccessFunction.form.create')}
        onInterfaceAdd={handleInterfaceAdd}
        onFieldUpdate={handleFieldUpdate}
        onClickSave={handleClickSave}
        onInterfaceRemove={handleInterfaceRemove}
        onClickCancelClose={handleCancel}
      />
    </div>
  );
};

export default withRouter(connector(TargetDeliveryTemplateAccessFunctionCreate));
